import { Chart, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { useMemo } from "react";
import annotationPlugin from "chartjs-plugin-annotation";
import { useState } from "react";
import { stringToColor } from "../utils/color";

// đăng ký component chartjs
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  Filler
);

const LineLegend = ({ datasets, onToggleDataset }) => {
  return (
    <div
      style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}
    >
      {datasets.map(({ label, borderColor, hidden }, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer", // Change cursor to indicate clickable
            width: "fit-content",
            flexWrap: "wrap",
            opacity: hidden ? 0.5 : 1, // Reduce opacity for hidden datasets
          }}
          onClick={() => onToggleDataset(idx)} // Add click handler
        >
          <div
            style={{
              width: 30,
              height: 8,
              backgroundColor: borderColor,
              borderRadius: 2,
              fontSize: 12,
            }}
          />
          <span style={{ fontSize: 12 }}>{label}</span>
        </div>
      ))}
    </div>
  );
};

const LegendCustom = ({
  types,
  typeToPointStyle,
  typeToColor,
  hiddenTypes,
  onToggleType,
}) => {
  return (
    <div
      style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}
    >
      {types.map((type) => {
        const pointStyle = typeToPointStyle[type];
        const isHidden = hiddenTypes.includes(type);

        return (
          <div
            key={type}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              width: "fit-content",
              opacity: isHidden ? 0.5 : 1,
            }}
            onClick={() => onToggleType(type)}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: typeToColor[type],
                clipPath: getClipPath(pointStyle),
                border: `1px solid ${typeToColor[type]}`,
              }}
              title={pointStyle}
            />
            <span style={{ fontSize: 12 }}>{type}</span>
          </div>
        );
      })}
    </div>
  );
};

// Hàm giả lập clipPath để mô phỏng hình dạng pointStyle đơn giản
// (Chart.js không cung cấp sẵn icon SVG cho pointStyle, nên ta làm dạng clipPath css đơn giản)
function getClipPath(pointStyle) {
  switch (pointStyle) {
    case "circle":
      return "circle(50% at 50% 50%)";
    case "rect":
      return "inset(15% round 2px)"; // Bigger rectangle
    case "triangle":
      return "polygon(50% 0%, 0% 100%, 100% 100%)";
    case "star":
      // 8-pointed star with 8 edges
      return "polygon(50% 0%, 65% 25%, 85% 15%, 75% 40%, 100% 50%, 75% 60%, 85% 85%, 65% 75%, 50% 100%, 35% 75%, 15% 85%, 25% 60%, 0% 50%, 25% 40%, 15% 15%, 35% 25%)";
    case "crossRot":
      // Thinner rotated cross (45 degrees) with 8 polygons
      return "polygon(45% 0%, 55% 0%, 70% 15%, 85% 0%, 100% 15%, 85% 30%, 100% 45%, 85% 60%, 100% 85%, 85% 100%, 70% 85%, 55% 100%, 45% 100%, 30% 85%, 15% 100%, 0% 85%, 15% 70%, 0% 55%, 15% 45%, 0% 30%, 15% 15%, 30% 0%)";
    case "cross":
      // Normal cross (reverted back to original size)
      return "polygon(40% 0%, 60% 0%, 60% 40%, 100% 40%, 100% 60%, 60% 60%, 60% 100%, 40% 100%, 40% 60%, 0% 60%, 0% 40%, 40% 40%)";
    case "rectRot":
      // Rotated rectangle (diamond shape)
      return "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)";
    default:
      return "circle(50% at 50% 50%)";
  }
}

const CustomChart = ({ chartData, eventData, showY2 = false }) => {
  const [hiddenDatasets, setHiddenDatasets] = useState([]);
  const [hiddenTypes, setHiddenTypes] = useState([]);

  // Function to check if all datasets of a specific type are hidden
  const areAllDatasetsOfTypeHidden = (condition) => {
    const datasetsOfType = chartData?.filter(condition);
    if (!datasetsOfType || datasetsOfType.length === 0) return true;

    // Check if all datasets matching the condition are in the hiddenDatasets array
    return datasetsOfType.every((dataset) => {
      const datasetIndex = chartData.indexOf(dataset);
      return datasetIndex !== -1 && hiddenDatasets.includes(datasetIndex);
    });
  };

  // Check if all gross data (y2 axis) datasets are hidden
  const areAllGrossDataHidden = () =>
    areAllDatasetsOfTypeHidden((ds) => ds.yAxisID === "y2");

  // Check if all visitor data (y axis) datasets are hidden
  const areAllVisitorDataHidden = () =>
    areAllDatasetsOfTypeHidden((ds) => ds.yAxisID === "y");

  // Function to toggle dataset visibility
  const handleToggleDataset = (index) => {
    setHiddenDatasets((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Function to toggle type visibility
  const handleToggleType = (type) => {
    setHiddenTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Function to toggle all datasets of a specific type
  const toggleDatasetGroup = (condition) => {
    const newHiddenDatasets = [...hiddenDatasets];
    const allHidden = chartData.filter(condition).every((ds, i) => {
      const index = chartData.findIndex((d) => d === ds);
      return hiddenDatasets.includes(index);
    });

    chartData.forEach((dataset, index) => {
      if (condition(dataset)) {
        const isHidden = newHiddenDatasets.includes(index);
        if (allHidden && isHidden) {
          // Remove from hidden if all are currently hidden
          const hiddenIndex = newHiddenDatasets.indexOf(index);
          if (hiddenIndex !== -1) {
            newHiddenDatasets.splice(hiddenIndex, 1);
          }
        } else if (!allHidden && !isHidden) {
          // Add to hidden if not all are currently hidden
          newHiddenDatasets.push(index);
        }
      }
    });

    setHiddenDatasets(newHiddenDatasets);
  };

  // Toggle all gross data (y2 axis)
  const toggleGrossData = () =>
    toggleDatasetGroup((dataset) => dataset.yAxisID === "y2");

  // Toggle all visitor data (y axis)
  const toggleVisitorData = () =>
    toggleDatasetGroup((dataset) => dataset.yAxisID === "y");

  const finalChartData = useMemo(() => {
    if (
      !eventData ||
      !eventData?.data ||
      eventData?.data?.length === 0 ||
      !chartData ||
      chartData.length === 0
    ) {
      return [];
    }

    const types = [...new Set(eventData?.data?.map((event) => event.type))];

    // This pointStyles object should match the one used in LegendCustom
    const pointStyles = {
      Reddit: "circle",
      Ads: "rect",
      LP: "triangle",
      YouTube: "circle",
      Instagram: "cross",
      Newsletter: "cross",
      Whatsapp: "rectRot",
      LinkedIn: "triangle",
      Discord: "rect",
      TikTok: "star",
      default: "circle", // fallback style
    };

    const typeToPointStyle = {};
    const typeToColor = {};
    types.forEach((type) => {
      typeToPointStyle[type] = pointStyles[type] || pointStyles["default"];
      typeToColor[type] = stringToColor(type);
    });

    // Apply hidden property to datasets
    const processedChartData = chartData.map((dataset, index) => ({
      ...dataset,
      hidden: hiddenDatasets.includes(index),
    }));

    // Filter events based on hidden types and create event datasets
    const events = eventData.data
      .filter((event) => !hiddenTypes.includes(event.type)) // Filter out hidden types
      .map((event) => {
        const color = typeToColor[event.type] || "#000000";
        return {
          ...eventData,
          data: [{ ...event }],
          label: "",
          pointStyle: typeToPointStyle[event.type] || "circle",
          pointBackgroundColor: color,
          pointBorderColor: color,
        };
      });

    return [...processedChartData, ...events];
  }, [chartData, eventData, hiddenDatasets, hiddenTypes]);

  // Remove the duplicate pointStyles and typeToPointStyle definitions
  // that were conflicting with the ones inside finalChartData

  const types = useMemo(
    () => [...new Set(eventData?.data?.map((event) => event.type))],
    [eventData]
  );

  // Create typeToPointStyle and typeToColor for the legend using the same mapping
  const legendTypeToPointStyle = {};
  const legendTypeToColor = {};

  // Use the same pointStyles mapping as in finalChartData
  const pointStylesForLegend = {
    Reddit: "circle",
    Ads: "rect",
    LP: "triangle",
    YouTube: "circle",
    Instagram: "cross",
    Newsletter: "cross",
    Whatsapp: "rectRot",
    LinkedIn: "triangle",
    Discord: "rect",
    TikTok: "star",
    default: "circle",
  };

  types.forEach((type) => {
    legendTypeToPointStyle[type] =
      pointStylesForLegend[type] || pointStylesForLegend["default"];
    legendTypeToColor[type] = stringToColor(type);
  });

  const mainLines = chartData?.filter(
    (ds) => ds.label && ds.borderColor && !ds.pointStyle
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false, // Allow custom height
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
        },
        ticks: {
          // Mobile-specific tick adjustments
          maxTicksLimit: window.innerWidth <= 768 ? 5 : 10,
          font: {
            size: window.innerWidth <= 768 ? 10 : 12,
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Visitor Count",
          font: {
            size: window.innerWidth <= 768 ? 11 : 12,
          },
        },
        ticks: {
          font: {
            size: window.innerWidth <= 768 ? 10 : 12,
          },
        },
      },
      ...(showY2
        ? {
            y2: {
              type: "linear",
              display: true,
              position: "right",
              grid: {
                drawOnChartArea: false,
              },
              title: {
                display: true,
                text: "KS Gross (€)",
                font: {
                  size: window.innerWidth <= 768 ? 11 : 12,
                },
              },
              stacked: true,
              ticks: {
                font: {
                  size: window.innerWidth <= 768 ? 10 : 12,
                },
              },
            },
          }
        : {}),
    },
    plugins: {
      legend: {
        display: false,
      },
      annotations: {
        adCampaignStart: {
          type: "line",
          xMin: "2025-02-01",
          xMax: "2025-02-01",
          borderColor: "red",
          borderWidth: 2,
          label: {
            content: "Ad campaign start",
            enabled: true,
            position: "start",
            color: "red",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            font: {
              style: "italic",
              weight: "bold",
              size: window.innerWidth <= 768 ? 10 : 12,
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        titleFont: {
          size: window.innerWidth <= 768 ? 11 : 12,
        },
        bodyFont: {
          size: window.innerWidth <= 768 ? 10 : 11,
        },
        callbacks: {
          title: function (context) {
            if (context[0]?.parsed?.x) {
              const date = new Date(context[0].parsed.x);
              return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            }
            return "";
          },
          label: function (context) {
            if (context?.dataset?.fill) {
              return context.dataset.label + ": " + context.parsed.y;
            }
            const eventName = context.dataset.data[0].name;
            return eventName;
          },
        },
      },
    },
  };

  return (
    <div className="chart-container">
      {/* Chart controls */}
      {showY2 && (
        <div
          className="chart-controls"
          style={{
            marginBottom: "10px",
            textAlign: "center",
            display: "flex",
            flexDirection: window.innerWidth <= 768 ? "column" : "row",
            gap: window.innerWidth <= 768 ? "8px" : "10px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            onClick={toggleVisitorData}
            style={{
              margin: window.innerWidth <= 768 ? "0" : "0 5px",
              padding: window.innerWidth <= 768 ? "8px 12px" : "5px 10px",
              fontSize: window.innerWidth <= 768 ? "14px" : "16px",
              backgroundColor: !areAllVisitorDataHidden()
                ? "#902A01"
                : "#EFA762",
              color: !areAllVisitorDataHidden() ? "white" : "black",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              minWidth: window.innerWidth <= 768 ? "140px" : "auto",
            }}
          >
            {!areAllVisitorDataHidden()
              ? "Hide Visitor Data"
              : "Show Visitor Data"}
          </button>
          <button
            onClick={toggleGrossData}
            style={{
              margin: window.innerWidth <= 768 ? "0" : "0 5px",
              padding: window.innerWidth <= 768 ? "8px 12px" : "5px 10px",
              fontSize: window.innerWidth <= 768 ? "14px" : "16px",
              backgroundColor: !areAllGrossDataHidden() ? "#902A01" : "#EFA762",
              color: !areAllGrossDataHidden() ? "white" : "black",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              minWidth: window.innerWidth <= 768 ? "140px" : "auto",
            }}
          >
            {!areAllGrossDataHidden() ? "Hide Gross Data" : "Show Gross Data"}
          </button>
        </div>
      )}

      <LineLegend
        datasets={mainLines.map((line, idx) => ({
          ...line,
          hidden: hiddenDatasets.includes(idx),
        }))}
        onToggleDataset={handleToggleDataset}
      />
      <LegendCustom
        types={types}
        typeToPointStyle={legendTypeToPointStyle}
        typeToColor={legendTypeToColor}
        hiddenTypes={hiddenTypes}
        onToggleType={handleToggleType}
      />

      {/* Chart wrapper with responsive height */}
      <div
        className="chart-wrapper-responsive"
        style={{
          height: window.innerWidth <= 768 ? "300px" : "400px",
          width: "100%",
          position: "relative",
        }}
      >
        <Line data={{ datasets: finalChartData }} options={options} />
      </div>
    </div>
  );
};

export default CustomChart;
